import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Card, Fade, Grid, Icon, Modal, Tooltip, IconButton, Divider } from "@mui/material";

// Componentes del Dashboard
import PieChart from "components/Charts/PieChart";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTableCustom from "components/Tables/DataTableCustom";
import CustomPagination from "components/Pagination";

// Contexto y Servicios
import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";
import { restLink } from "services/config";
import * as XLSX from "xlsx";
import { formatMoneyPunto } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { UserContext } from "context/user";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "96%", md: "90%", xl: "75%" },
  maxHeight: "92vh",
  display: "flex",
  flexDirection: "column",
  outline: "none",
  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  borderRadius: "16px",
  overflow: "hidden",
  backgroundColor: "#fff",
};

const vouchersPalette = {
  total: "#32507A",
  usado: "#3D9C4A",
  pendiente: "#F29C2B",
  vencido: "#D64545",
};

// --- Sub-componente: Card Compacta ---
function TotalesCard({ title, value, color, icon }) {
  return (
    <Card
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.05)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 4px 8px rgba(0,0,0,0.08)" },
      }}
    >
      <SoftBox p={1.5} display="flex" alignItems="center" gap={1.5}>
        <SoftBox
          variant="gradient"
          sx={{
            width: "38px",
            height: "38px",
            borderRadius: "10px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: color,
            flexShrink: 0,
            boxShadow: `0 4px 8px ${color}33`,
          }}
        >
          <Icon fontSize="small" sx={{ color: "#fff" }}>{icon}</Icon>
        </SoftBox>

        <SoftBox display="flex" flexDirection="column" lineHeight={1}>
          <SoftTypography variant="caption" color="text" fontWeight="bold" sx={{ fontSize: "0.7rem", textTransform: "uppercase", opacity: 0.7 }}>
            {title}
          </SoftTypography>
          <SoftTypography variant="h5" fontWeight="bold">
            {value}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

TotalesCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};

// --- COMPONENTE PRINCIPAL ---
export default function ModalReporteVouchers({ open, handleClose }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { menu } = useContext(MenuContext);
  const { user } = useContext(UserContext);
  const sedeId = useMemo(() => {
    const rawId = user?.sede?.id;
    return rawId == null ? undefined : String(rawId);
  }, [user?.sede?.id]);

  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("todos");
  const [expandEstadoActive, setExpandEstadoActive] = useState(false);

  const [reportData, setReportData] = useState({
    resumen: {},
    paginacion: { page: 1, limit: 10, pages: 0, total: 0 },
    totalesFiltrados: { montoTotalUsados: 0 },
    clientes: [],
  });

  const estadosVoucherOptions = [
    { id: "todos", nombre: "Todos", icon: "rule" },
    { id: "usado", nombre: "Usado", icon: "check_circle" },
    { id: "pendiente", nombre: "Pendiente", icon: "schedule" },
    { id: "vencido", nombre: "Vencido", icon: "event_busy" },
  ];

  // Función de carga envuelta en useCallback para estabilidad y soporte de AbortSignal
  const fetchReporteVouchers = useCallback(async (signal = null) => {
    try {
      setLoading(true);
      const response = await restLink.get("reportes/vouchers-alumnos", {
        signal, // Importante para cancelar peticiones previas
        params: {
          page,
          search: searchDebounced || undefined,
          estado: estadoSeleccionado,
          idSede: sedeId,
        },
      });

      setReportData(response?.data || {
        resumen: {},
        paginacion: { page: 1, limit: 10, pages: 0, total: 0 },
        totalesFiltrados: { montoTotalUsados: 0 },
        clientes: [],
      });
    } catch (error) {
      // Si el error es por cancelación, no mostramos el snackbar
      if (error.name === "CanceledError" || error.message === "canceled") return;
      handleSnackbar("No se pudo obtener el reporte", "error");
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced, estadoSeleccionado, sedeId, handleSnackbar]);

  // Manejo del Excel
  const handleDownloadRawExcel = async () => {
    if (loading) return;
    try {
      const response = await restLink.get("reportes/vouchers-alumnos-excel", {
        params: {
          search: searchDebounced || undefined,
          estado: estadoSeleccionado,
          idSede: sedeId,
        },
      });
      const payload = response?.data || {};

      const resumen = payload?.resumen || {};
      const totalesFiltrados = payload?.totalesFiltrados || {};
      const clientesRaw = Array.isArray(payload?.clientes) ? payload.clientes : [];

      if (!Object.keys(resumen).length && !clientesRaw.length) {
        handleSnackbar("No hay datos para exportar", "warning");
        return;
      }

      const wb = XLSX.utils.book_new();
      const resumenRows = Object.entries(resumen).map(([campo, valor]) => ({ campo, valor }));
      const filtrosRows = [
        { campo: "estado", valor: estadoSeleccionado },
        { campo: "search", valor: searchDebounced || "" },
        { campo: "montoTotalUsadosFiltrados", valor: Number(totalesFiltrados?.montoTotalUsados || 0) },
      ];

      const clientesRows = clientesRaw.map((c) => ({
        ...c,
        estadoVoucher: c?.estadoVoucher || "-",
        usoVoucher: c.usoVoucher ? "SI" : "NO",
        fechaVtoVoucher: c.fechaVtoVoucher ? new Date(c.fechaVtoVoucher).toLocaleDateString() : "-",
        fechaVenta: c.fechaVenta ? new Date(c.fechaVenta).toLocaleString() : "-",
      }));

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumenRows), "resumen");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filtrosRows), "filtros");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(clientesRows), "clientes");

      XLSX.writeFile(wb, `reporte_vouchers_${new Date().getTime()}.xlsx`);
      handleSnackbar("Excel generado correctamente", "success");
    } catch (error) {
      handleSnackbar("Error al exportar Excel", "error");
    }
  };

  const handleOpenPdfUsoTotal = () => {
    if (loading) return;
    window.open("/vouchers-alumnos/pdf/uso-total", "_blank", "noopener,noreferrer");
  };

  // EFECTO 1: Debounce del buscador
  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentSearch = searchInput.trim();
      if (currentSearch !== searchDebounced) {
        setPage(1); // Reset de página solo si cambia el término
        setSearchDebounced(currentSearch);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchInput, searchDebounced]);

  // EFECTO 2: Llamada a la API con limpieza (Cleanup)
  useEffect(() => {
    const controller = new AbortController();

    if (open) {
      fetchReporteVouchers(controller.signal);
    } else {
      // Limpieza de estados al cerrar para que el modal esté "fresco" al reabrir
      setSearchInput("");
      setSearchDebounced("");
      setPage(1);
      setEstadoSeleccionado("todos");
    }

    return () => controller.abort(); // Cancela peticiones si el componente se desmonta o cambian dependencias
  }, [open, fetchReporteVouchers]);

  // Memorización de datos para la UI
  const rows = useMemo(() => Array.isArray(reportData?.clientes) ? reportData.clientes : [], [reportData]);
  const montoTotalVouchersUsados = useMemo(() => Number(reportData?.totalesFiltrados?.montoTotalUsados || 0), [reportData]);

  const columns = useMemo(() => [
    {
      field: "cliente",
      headerName: "Alumno / DNI",
      width: "40%",
      renderCell: (row) => (
        <SoftBox display="flex" flexDirection="column" gap={0.5}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ lineHeight: 1 }}>
            {row.apellido}, {row.nombre}
          </SoftTypography>
          <SoftTypography variant="caption" color="secondary">
            {row.dni}
          </SoftTypography>
        </SoftBox>
      ),
    },
    {
      field: "estadoVoucher",
      headerName: "Estado",
      align: "center",
      width: "30%",
      renderCell: (row) => {
        const estado = row?.estadoVoucher || (row?.usoVoucher ? "usado" : "pendiente");
        const stateStyles = {
          usado: { bg: "#e8f5e9", color: "#2e7d32", border: "#c8e6c9", label: "USADO" },
          pendiente: { bg: "#fff3e0", color: "#ef6c00", border: "#ffe0b2", label: "PENDIENTE" },
          vencido: { bg: "#fdecea", color: "#c62828", border: "#f5c6cb", label: "VENCIDO" },
        }[estado] || { bg: "#fff3e0", color: "#ef6c00", border: "#ffe0b2", label: "PENDIENTE" };

        return (
          <SoftBox
            sx={{
              backgroundColor: stateStyles.bg,
              color: stateStyles.color,
              px: 1.5, py: 0.4,
              borderRadius: "6px",
              fontSize: "0.7rem",
              fontWeight: "bold",
              display: "inline-block",
              border: `1px solid ${stateStyles.border}`,
            }}
          >
            {stateStyles.label}
          </SoftBox>
        );
      },
    },
    {
      field: "nroPedido",
      headerName: "Nro Pedido",
      align: "center",
      renderCell: (row) => (
        <SoftTypography variant="button" color="secondary">
          {row.nroPedido || "—"}
        </SoftTypography>
      ),
    },
    {
      field: "montoPedido",
      headerName: "Monto pedido",
      align: "right",
      width: "18%",
      renderCell: (row) => (
        <SoftTypography variant="button" color="secondary" fontWeight="medium">
          ${formatMoneyPunto(Number(row?.montoPedido || 0))}
        </SoftTypography>
      ),
    },
  ], []);

  const { totalVouchers = 0, totalUsado = 0, totalPendientes = 0, totalVencidos = 0 } = reportData.resumen || {};

  const cardsData = [
    { key: "total", title: "Total Generado", value: totalVouchers, color: vouchersPalette.total, icon: "confirmation_number" },
    { key: "usado", title: "Vouchers Usados", value: totalUsado, color: vouchersPalette.usado, icon: "check_circle" },
    { key: "pendiente", title: "Vouchers Pendientes", value: totalPendientes, color: vouchersPalette.pendiente, icon: "schedule" },
    { key: "vencido", title: "Vouchers Vencidos", value: totalVencidos, color: vouchersPalette.vencido, icon: "event_busy" },
  ];

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={modalStyle}>
          {/* HEADER */}
          <SoftBox p={2.5} display="flex" justifyContent="space-between" alignItems="center">
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold" color="dark">Reporte de Vouchers</SoftTypography>
              <SoftTypography variant="caption" color="text">Monitoreo de uso y disponibilidad</SoftTypography>
            </SoftBox>
            <SoftBox display="flex" gap={1}>
              <Tooltip title="Descargar datos crudos en Excel">
                <SoftButton variant="outlined" color="success" size="small" onClick={handleDownloadRawExcel} disabled={loading}>
                  <Icon>download</Icon>&nbsp;Excel
                </SoftButton>
              </Tooltip>
              <Tooltip title="Descargar PDF">
                <SoftButton color="info" size="small" onClick={handleOpenPdfUsoTotal} disabled={loading}>
                  <Icon>picture_as_pdf</Icon>&nbsp;PDF Uso total vouchers
                </SoftButton>
              </Tooltip>
              <Tooltip title="Actualizar">
                <IconButton onClick={() => fetchReporteVouchers()} disabled={loading} size="small" sx={{ border: "1px solid #ececec" }}>
                  <Icon color="info">refresh</Icon>
                </IconButton>
              </Tooltip>
              <SoftButton variant="gradient" color="dark" size="small" onClick={handleClose}>Cerrar</SoftButton>
            </SoftBox>
          </SoftBox>

          <Divider sx={{ my: 0, opacity: 0.1 }} />

          {/* BODY */}
          <SoftBox sx={{ overflowY: "auto", flex: 1, p: 3 }}>
            {loading && !rows.length ? (
              <SoftBox py={10}><Loading /></SoftBox>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <SoftBox display="flex" flexDirection="column" gap={1.5}>
                    {cardsData.map((card) => (
                      <TotalesCard key={card.key} {...card} />
                    ))}
                  </SoftBox>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Card sx={{ height: "100%", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", justifyContent: "center", p: 2, boxShadow: "none", border: "1px solid #f0f2f5" }}>
                    <SoftBox sx={{ width: "100%", maxWidth: "180px" }}>
                      <PieChart
                        chart={{
                          labels: ["Usado", "Pendiente", "Vencido"],
                          datasets: {
                            backgroundColors: [vouchersPalette.usado, vouchersPalette.pendiente, vouchersPalette.vencido],
                            data: [totalUsado, totalPendientes, totalVencidos],
                          }
                        }}
                      />
                    </SoftBox>
                    <SoftBox ml={{ sm: 4 }} mt={{ xs: 2, sm: 0 }} display="flex" flexDirection="column" gap={1}>
                      {cardsData.map((item) => (
                        <SoftBox key={item.key} display="flex" alignItems="center" gap={1}>
                          <SoftBox sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.color }} />
                          <SoftTypography variant="caption" fontWeight="bold" color="dark">
                            {item.value} <span style={{ fontWeight: "normal", color: "#67748e" }}>{item.title}</span>
                          </SoftTypography>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <SoftBox mt={1} display="flex" flexDirection="column" gap={2}>
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                      <SoftTypography variant="h6" fontWeight="bold">Detalle por Alumno</SoftTypography>
                      <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap" width={{ xs: "100%", md: "auto" }}>
                        <SoftButton
                          color="uproGreen"
                          circular
                          onClick={(event) => {
                            setExpandEstadoActive(true);
                            menu({
                              open: event.currentTarget,
                              align: "right",
                              options: estadosVoucherOptions.map((e) => ({
                                name: e.nombre,
                                icon: { icon: e.icon, color: getUproColor(estadoSeleccionado === e.id ? "uproGreen" : "uproBlue") },
                                onClick: () => { setPage(1); setEstadoSeleccionado(e.id); },
                              })),
                              onClose: () => setExpandEstadoActive(false),
                            });
                          }}
                        >
                          {estadosVoucherOptions.find((e) => e.id === estadoSeleccionado)?.nombre || "Todos"}
                          &nbsp;
                          <Icon sx={{ rotate: expandEstadoActive ? "180deg" : "0deg", transition: "0.3s" }}>expand_more</Icon>
                        </SoftButton>

                        <SoftBox width={{ xs: "100%", md: "300px" }}>
                          <SoftInput
                            placeholder="Buscar por nombre o DNI..."
                            icon={{ component: "search", direction: "left" }}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            size="small"
                          />
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>

                    <SoftBox sx={{ minHeight: "350px", border: "1px solid #f0f2f5", borderRadius: "12px", overflow: "hidden" }}>
                      {rows.length > 0 ? (
                        <DataTableCustom
                          columns={columns}
                          data={rows}
                          showActions={false}
                          footerContent={(
                            <>
                              <SoftTypography variant="h5" color="secondary" fontWeight="bold">
                                Monto total usados: ${formatMoneyPunto(montoTotalVouchersUsados)}
                              </SoftTypography>
                              <SoftTypography variant="caption" color="text">
                                {`Registros filtrados: ${reportData?.paginacion?.total || 0}`}
                              </SoftTypography>
                            </>
                          )}
                        />
                      ) : (
                        <SinDatos />
                      )}
                    </SoftBox>

                    <CustomPagination
                      loading={loading}
                      length={rows.length}
                      total={reportData?.paginacion?.total || 0}
                      pages={reportData?.paginacion?.pages || 0}
                      page={page}
                      setPage={setPage}
                      noCard
                    />
                  </SoftBox>
                </Grid>
              </Grid>
            )}
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalReporteVouchers.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
